import type { Table, TableShape, SeatingChart, Seat } from "../../types/models";

/**
 * Distance matrix for calculating seat distances (pixel-based)
 */
export interface DistanceMatrix {
    /** Get pixel distance between two seats */
    get(seatIdA: string, seatIdB: string): number;
    /** Get all seat IDs in the matrix */
    getAllSeatIds(): string[];
}

/**
 * Get the size of a table (width and height)
 */
function getTableSize(table: Table): { width: number; height: number } {
    const baseSize = 80 + table.seats.length * 25;

    if (table.shape === 'round') {
        const diameter = table.width ?? baseSize;
        return { width: diameter, height: table.height ?? diameter };
    }

    // Rectangular
    const width = table.width ?? baseSize * 1.4;
    const height = table.height ?? baseSize * 0.8;
    return { width, height };
}

/**
 * Calculate seat positions around a table (relative to table top-left)
 */
function calculateSeatPositions(
    shape: TableShape,
    seats: Seat[],
    size: { width: number; height: number }
): { x: number; y: number }[] {
    const positions: { x: number; y: number }[] = [];
    const cx = size.width / 2;
    const cy = size.height / 2;

    for (let i = 0; i < seats.length; i++) {
        const seat = seats[i];

        // Use custom position if set
        if (seat && seat.x !== undefined && seat.y !== undefined) {
            positions.push({ x: seat.x, y: seat.y });
            continue;
        }

        if (shape === 'round') {
            const angle = (i / seats.length) * Math.PI * 2 - Math.PI / 2;
            const radius = Math.min(cx, cy) + 20;
            positions.push({
                x: cx + Math.cos(angle) * radius,
                y: cy + Math.sin(angle) * radius
            });
        } else {
            // Rectangular - distribute around perimeter
            const perimeter = 2 * (size.width + size.height);
            const spacing = perimeter / seats.length;
            const dist = i * spacing;

            let x: number, y: number;
            if (dist < size.width) {
                x = dist;
                y = -20;
            } else if (dist < size.width + size.height) {
                x = size.width + 20;
                y = dist - size.width;
            } else if (dist < 2 * size.width + size.height) {
                x = size.width - (dist - size.width - size.height);
                y = size.height + 20;
            } else {
                x = -20;
                y = size.height - (dist - 2 * size.width - size.height);
            }
            positions.push({ x, y });
        }
    }

    return positions;
}

/**
 * Get absolute pixel position of a seat in the chart
 */
function getSeatAbsolutePosition(table: Table, seatIndex: number): { x: number; y: number } | null {
    const seat = table.seats[seatIndex];
    if (!seat) return null;

    const size = getTableSize(table);
    const positions = calculateSeatPositions(table.shape, table.seats, size);
    const pos = positions[seatIndex];

    if (!pos) return null;

    return {
        x: table.x + pos.x,
        y: table.y + pos.y
    };
}

/**
 * Build a pixel-based distance matrix for all seats in a seating chart
 */
export function buildDistanceMatrix(chart: SeatingChart): DistanceMatrix {
    // Map from seatId to absolute pixel position
    const seatPositions = new Map<string, { x: number; y: number }>();

    for (const table of chart.tables) {
        const size = getTableSize(table);
        const positions = calculateSeatPositions(table.shape, table.seats, size);

        for (let i = 0; i < table.seats.length; i++) {
            const seat = table.seats[i];
            const pos = positions[i];
            if (seat && pos) {
                seatPositions.set(seat.id, {
                    x: table.x + pos.x,
                    y: table.y + pos.y
                });
            }
        }
    }

    return {
        get(seatIdA: string, seatIdB: string): number {
            if (seatIdA === seatIdB) return 0;

            const posA = seatPositions.get(seatIdA);
            const posB = seatPositions.get(seatIdB);

            if (!posA || !posB) return Infinity;

            // Calculate Euclidean pixel distance
            const dx = posA.x - posB.x;
            const dy = posA.y - posB.y;
            return Math.sqrt(dx * dx + dy * dy);
        },

        getAllSeatIds(): string[] {
            return Array.from(seatPositions.keys());
        }
    };
}

/**
 * Calculate the Euclidean distance between two tables (center to center)
 */
export function tableCenterDistance(tableA: Table, tableB: Table): number {
    const dx = tableA.x - tableB.x;
    const dy = tableA.y - tableB.y;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Find all seats within a given pixel distance from a specific seat
 */
export function getSeatsWithinDistance(
    matrix: DistanceMatrix,
    seatId: string,
    maxDistance: number
): string[] {
    return matrix.getAllSeatIds().filter(otherId => {
        if (otherId === seatId) return false;
        return matrix.get(seatId, otherId) <= maxDistance;
    });
}

/**
 * Find all seats at least a given pixel distance from a specific seat
 */
export function getSeatsAtLeastDistance(
    matrix: DistanceMatrix,
    seatId: string,
    minDistance: number
): string[] {
    return matrix.getAllSeatIds().filter(otherId => {
        if (otherId === seatId) return false;
        const dist = matrix.get(seatId, otherId);
        return dist >= minDistance || dist === Infinity;
    });
}
